import { apiClient } from './apiClient';

export interface IntegrationConfig {
  [key: string]: string;
}

export interface WFirmaImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: number;
  errorMessages: string[];
}

export interface BulkInvoiceSyncResult {
  totalSelected: number;
  success: number;
  skippedAlreadySynced: number;
  failed: number;
  errorMessages: string[];
}

export interface BulkInvoiceResetResult {
  totalSelected: number;
  resetToDraft: number;
  skippedByStatus: number;
}

export interface VatCodesCheckResult {
  status: string;
  message: string;
  totalCount: number;
  hasRate23: boolean;
  hasRate8: boolean;
  hasRate0: boolean;
  hasZw: boolean;
  hasNp: boolean;
  hasNpue: boolean;
  vatCodes: Array<{
    wfirmaId: number;
    code?: string;
    name?: string;
    rate?: number;
    isEu?: boolean;
  }>;
}

export interface SyncInvoicesPeriodRequest {
  startDate: string;
  endDate: string;
  forceResync?: boolean;
  /** ISSUE — дата выставления, SALE — дата продажи */
  dateField?: 'ISSUE' | 'SALE';
  /** Список статусов для операций массового сброса в черновик */
  statuses?: string[];
}

export const integrationService = {
  // Получить список всех типов интеграций для компании
  getIntegrationTypes: async (companyId: string): Promise<string[]> => {
    const response = await apiClient.get(`/companies/${companyId}/integrations`);
    return response.data;
  },

  // Получить конфигурацию интеграции
  getIntegrationConfig: async (companyId: string, integrationType: string): Promise<IntegrationConfig> => {
    const response = await apiClient.get(`/companies/${companyId}/integrations/${integrationType}/config`);
    return response.data;
  },

  // Сохранить конфигурацию интеграции
  saveIntegrationConfig: async (companyId: string, integrationType: string, config: IntegrationConfig): Promise<void> => {
    await apiClient.post(`/companies/${companyId}/integrations/${integrationType}/config`, config);
  },

  // Удалить конфигурацию интеграции
  deleteIntegrationConfig: async (companyId: string, integrationType: string): Promise<void> => {
    await apiClient.delete(`/companies/${companyId}/integrations/${integrationType}/config`);
  },

  // Проверить наличие конфигурации wFirma
  hasWFirmaConfig: async (companyId: string): Promise<boolean> => {
    try {
      await apiClient.get(`/companies/${companyId}/integrations/wfirma/config`);
      return true;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  },

  // Импортировать контрагентов из wFirma
  importContractorsFromWFirma: async (companyId: string): Promise<WFirmaImportResult> => {
    const response = await apiClient.post(`/companies/${companyId}/wfirma/import-contractors`);
    return response.data;
  },

  /** Массовая отправка фактур в wFirma за период */
  syncInvoicesPeriod: async (companyId: string, body: SyncInvoicesPeriodRequest): Promise<BulkInvoiceSyncResult> => {
    const response = await apiClient.post(`/companies/${companyId}/wfirma/invoices/sync-period`, body);
    return response.data;
  },

  /** Массовый перевод отправленных фактур обратно в черновики */
  resetInvoicesToDraftPeriod: async (
    companyId: string,
    body: SyncInvoicesPeriodRequest
  ): Promise<BulkInvoiceResetResult> => {
    const response = await apiClient.post(`/companies/${companyId}/wfirma/invoices/reset-to-draft-period`, body);
    return response.data;
  },

  /** Обновить VAT коды из wFirma и проверить базовые ставки/коды */
  refreshAndCheckVatCodes: async (companyId: string): Promise<VatCodesCheckResult> => {
    const response = await apiClient.post(`/companies/${companyId}/wfirma/vat-codes/refresh-check`);
    return response.data;
  }
};
